import type { RowData } from "@tanstack/table-core";
import type { MRT_Column, MRT_RowData } from "material-react-table";
import type { TableColumnKind } from "_shared/_views/tables/table-column-fit";

/**
 * How a list table's row reads as a card on a phone (#6715), worked out from the columns the table
 * already describes, so no page writes a card of its own.
 *
 * - The **title** is the first `name` column, drawn by its own `Cell`: the link, and the second line
 *   under it. A table with no `name` column takes its first data column.
 * - The **status** is the first `status` column that never folds. A column that folds is an aside to
 *   another column, and reads as a field instead.
 * - The **fields** are up to {@link CARD_FIELD_COUNT} of the rest, the most important first. A column
 *   that never folds (`meta.fold`) outranks one that does, since the fit gives it up last; among the
 *   columns that fold, the one with the higher `fold.priority` folds later, so it ranks higher. Ties keep
 *   the table's column order.
 *
 * Only the columns the reader shows count, so Show/hide decides what a card carries, as it decides what
 * the table draws. The select and actions columns are the card's own controls, not fields.
 */

/**
 * A grouping a column offers besides its sort, such as the proposals RFP column's Group by RFP. The
 * desktop offers it in the column's own menu; a card has no column menu, so the phone's Sort sheet lists
 * it from here (#6715). `isGrouped` says whether it is on, and `onToggle` turns it on or off.
 */
export type TableColumnGrouping = {
  label: string;
  isGrouped: boolean;
  onToggle: () => void;
};

declare module "@tanstack/table-core" {
  // `TData` and `TValue` must match TanStack's own declaration for the merge to apply.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** The grouping the column offers, for a sheet that has no column menu. See {@link TableColumnGrouping}. */
    group?: TableColumnGrouping;
  }
}

/** How many fields a card shows under its title and status. */
export const CARD_FIELD_COUNT = 3;

export type TableCardLayout<TData extends MRT_RowData> = {
  title?: MRT_Column<TData>;
  status?: MRT_Column<TData>;
  fields: MRT_Column<TData>[];
};

function kindOf<TData extends MRT_RowData>(column: MRT_Column<TData>): TableColumnKind | undefined {
  return column.columnDef.meta?.kind;
}

/** A column that holds a value of the row, rather than a control MRT adds (select, actions, expand). */
export function isDataColumn<TData extends MRT_RowData>(column: MRT_Column<TData>): boolean {
  return !column.id.startsWith("mrt-") && kindOf(column) !== "actions";
}

/** Higher is more important: a column that never folds, then the latest to fold. */
function rankOf<TData extends MRT_RowData>(column: MRT_Column<TData>): number {
  const fold = column.columnDef.meta?.fold;
  return fold ? fold.priority : Number.POSITIVE_INFINITY;
}

export function tableCardLayout<TData extends MRT_RowData>(
  visibleColumns: readonly MRT_Column<TData>[],
): TableCardLayout<TData> {
  const columns = visibleColumns.filter(isDataColumn);
  const title = columns.find((column) => kindOf(column) === "name") ?? columns[0];
  const status = columns.find(
    (column) => column !== title && kindOf(column) === "status" && !column.columnDef.meta?.fold,
  );
  const fields = columns
    .filter((column) => column !== title && column !== status)
    .map((column, order) => ({ column, order, rank: rankOf(column) }))
    .sort((a, b) => b.rank - a.rank || a.order - b.order)
    .slice(0, CARD_FIELD_COUNT)
    .map(({ column }) => column);
  return { title, status, fields };
}

/** Every grouping the table's columns offer, shown or hidden: a hidden column can still group. */
export function columnGroupings<TData extends MRT_RowData>(
  columns: readonly MRT_Column<TData>[],
): TableColumnGrouping[] {
  return columns.flatMap((column) => (column.columnDef.meta?.group ? [column.columnDef.meta.group] : []));
}

/** A column's heading as words, for a card's field label and the sheets' lists. */
export function columnLabel<TData extends MRT_RowData>(column: MRT_Column<TData>): string {
  const { header } = column.columnDef;
  return typeof header === "string" && header.length > 0 ? header : column.id;
}

/** The two directions a column sorts in, named for what it holds: "A to Z", "Earliest first". */
export function sortDirectionLabels<TData extends MRT_RowData>(
  column: MRT_Column<TData>,
): { ascending: string; descending: string } {
  switch (kindOf(column)) {
    case "date":
    case "dateRange":
    case "timestamp":
      return { ascending: "Earliest first", descending: "Latest first" };
    case "number":
    case "money":
      return { ascending: "Lowest first", descending: "Highest first" };
    default:
      return { ascending: "A to Z", descending: "Z to A" };
  }
}
